import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import { Building2, Crown, Sparkles, Check, ArrowRight } from 'lucide-react';

const PartnerWithUs = () => {
  const navigate = useNavigate();
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleExplorePartnerships = () => {
    if (user) {
      // User is logged in, go to detailed packages page
      navigate('/become-partner');
    } else {
      // User not logged in, prompt to sign up with redirect parameter
      navigate('/?auth=signup&redirect=become-partner');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Partner With Us
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Elevate your brand and reach thousands of ambitious women entrepreneurs through our strategic partnership opportunities.
          </p>
        </div>

        {/* Partnership Tiers */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {/* Silver Partner */}
          <Card className="glass hover:shadow-xl transition-all group">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Building2 className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Silver Partner
              </h3>
              <p className="text-gray-600 mb-6 text-sm">Perfect for growing businesses</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Logo featured on homepage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Event sponsorship opportunity</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Analytics dashboard access</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Member directory listing</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Gold Partner */}
          <Card className="glass hover:shadow-xl transition-all border-2 border-rose-500 group scale-105 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
            </div>
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Crown className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Gold Partner
              </h3>
              <p className="text-gray-600 mb-6 text-sm">Ideal for established brands</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Premium homepage placement</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Multiple event sponsorships</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Featured content opportunities</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Advanced analytics & insights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Newsletter feature</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Platinum Partner */}
          <Card className="glass hover:shadow-xl transition-all group">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Platinum Partner
              </h3>
              <p className="text-gray-600 mb-6 text-sm">Ultimate brand visibility</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Exclusive homepage banner</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Unlimited event sponsorships</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Dedicated content series</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Priority member access</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Custom integration options</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Card className="glass">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Ready to Explore Partnership Opportunities?
              </h2>
              <p className="text-gray-700 mb-8">
                {user 
                  ? "View detailed packages, pricing, and choose the partnership tier that fits your business goals."
                  : "Sign up as a free member to view detailed packages, pricing, and custom partnership options tailored to your business goals."
                }
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  onClick={handleExplorePartnerships} 
                  className="bg-rose-600 hover:bg-rose-700 px-8 py-6 text-lg"
                >
                  {user ? (
                    <>
                      View Packages & Pricing
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    "Join Free & Explore"
                  )}
                </Button>
                <Button 
                  onClick={() => navigate('/contact')}
                  variant="outline" 
                  className="border-rose-600 text-rose-600 hover:bg-rose-50 px-8 py-6 text-lg"
                >
                  Contact Us
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Why Partner With Us */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Why Partner With Her Enterprise Hub?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="glass">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2 text-rose-700">Targeted Audience</h3>
                <p className="text-gray-700 text-sm">
                  Connect with thousands of ambitious women entrepreneurs and business owners actively seeking growth opportunities.
                </p>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2 text-rose-700">Brand Visibility</h3>
                <p className="text-gray-700 text-sm">
                  Increase your brand awareness through homepage features, event sponsorships, and content opportunities.
                </p>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2 text-rose-700">Meaningful Impact</h3>
                <p className="text-gray-700 text-sm">
                  Support women in business while growing your own brand and contributing to a thriving community.
                </p>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2 text-rose-700">Analytics & Insights</h3>
                <p className="text-gray-700 text-sm">
                  Track your partnership ROI with detailed analytics and member engagement insights.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerWithUs;
